import { useEffect } from 'react';

/**
 * SEO Component - Updates meta tags dynamically without using Helmet
 * @param {Object} props - SEO properties
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - Page keywords (comma-separated)
 * @param {string} props.canonical - Canonical URL
 * @param {string} props.ogTitle - Open Graph title (defaults to title)
 * @param {string} props.ogDescription - Open Graph description (defaults to description)
 * @param {string} props.ogImage - Open Graph image URL
 * @param {string} props.ogUrl - Open Graph URL (defaults to canonical)
 * @param {Object} props.structuredData - JSON-LD structured data object
 */
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  structuredData
}) => {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      if (!content) return;
      
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (element) {
        element.setAttribute('content', content);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        element.setAttribute('content', content);
        document.head.appendChild(element);
      }
    };

    // Update canonical link
    const updateCanonical = (url) => {
      if (!url) return;
      
      let link = document.querySelector('link[rel="canonical"]');
      if (link) {
        link.setAttribute('href', url);
      } else {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', url);
        document.head.appendChild(link);
      }
    };

    // Update structured data
    const updateStructuredData = (data) => {
      if (!data) return;
      
      // Remove existing structured data script if present
      const existingScript = document.querySelector('script[data-seo-structured-data]');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Add new structured data
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo-structured-data', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };

    // Update all meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Open Graph tags
    updateMetaTag('og:title', ogTitle || title, true);
    updateMetaTag('og:description', ogDescription || description, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:url', ogUrl || canonical, true);
    
    // Twitter tags
    updateMetaTag('twitter:title', ogTitle || title);
    updateMetaTag('twitter:description', ogDescription || description);
    updateMetaTag('twitter:image', ogImage);
    
    // Canonical URL
    updateCanonical(canonical);
    
    // Structured Data
    updateStructuredData(structuredData);

    // Cleanup function - restore default title on unmount
    return () => {
      document.title = 'Tuomas - Expert Expat Relocation, UAE Company Setup & International Services';
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogUrl, structuredData]);

  return null; // This component doesn't render anything
};

export default SEO;

